import React, { forwardRef, useCallback, useMemo } from 'react';
import { Link as ExpoLink, Redirect as ExpoRedirect, router as expoRouter, useFocusEffect, useRouter as useExpoRouter } from 'expo-router';
import { publicDocumentHref, usesPublicDocuments } from './publicPageNavigation';

function documentHref(href) {
  if (!usesPublicDocuments()) return null;
  const resolved = ExpoLink.resolveHref(href);
  // Relative routes need Expo's active navigator and relativeToDirectory rules.
  if (resolved.startsWith('.')) return null;
  return publicDocumentHref(resolved);
}

function documentRouter(source) {
  const navigate = method => (href, ...args) => {
    const destination = documentHref(href);
    if (destination) {
      window.location[method === 'replace' ? 'replace' : 'assign'](destination);
      return;
    }
    return source[method](href, ...args);
  };
  return {
    ...source,
    push: navigate('push'),
    navigate: navigate('navigate'),
    replace: navigate('replace'),
    dismissTo: navigate('dismissTo'),
  };
}

// Decide ownership before changing Expo's navigation state. The route-level
// handoff remains a safety net, rather than the normal path out of the app.
export const router = documentRouter(expoRouter);
export function useRouter() {
  const source = useExpoRouter();
  return useMemo(() => documentRouter(source), [source]);
}

export function Redirect({ href, ...props }) {
  const destination = documentHref(href);
  useFocusEffect(useCallback(() => {
    if (destination) window.location.replace(destination);
  }, [destination]));
  return destination ? null : <ExpoRedirect {...props} href={href} />;
}

export const Link = forwardRef(function DocumentLink({ href, ...props }, ref) {
  const destination = documentHref(href);
  // In particular, leave Expo's asChild onPress/onClick composition intact for
  // app links; native browser navigation is only substituted for Pug pages.
  if (!destination) return <ExpoLink {...props} href={href} ref={ref} />;
  const { onPress, ...rest } = props;
  return <ExpoLink {...rest} href={href} ref={ref} target={props.target ?? (props.download != null ? '_self' : undefined)} prefetch={false}
    onPress={event => {
      onPress?.(event);
      // Leave modified clicks, other targets and downloads to the browser.
      // Child handlers can still cancel navigation through preventDefault.
      const target = event.currentTarget?.getAttribute('target');
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
        || (event.button != null && event.button !== 0) || (target && target !== '_self')
        || event.currentTarget?.hasAttribute('download')) return;
      event.preventDefault();
      window.location.assign(destination);
    }} />;
});
